/* eslint @typescript-eslint/explicit-module-boundary-types: off */
import axiosInstance from './axios';
import PaginationData from 'types/models/PaginationData';
import CloverPointConfig from 'types/models/CloverPointConfig';
import CloverPoint from 'types/models/CloverPoint';
import CashBox from 'types/models/CashBox';
import CashBoxEvent from 'types/models/CashBoxEvent';
import CashBoxEventsTypes from 'types/enums/CashBoxEventsTypes';
import CashBoxAction from 'types/models/CashBoxAction';
import CashBoxTransaction from 'types/models/CashBoxTransaction';
import { parseOrder, parseOrders } from './orderServices';
import {
  CashBoxCloseResponse,
  CashBoxCloseCategoryGroup,
  CashBoxCloseProduct,
} from 'types/models/CashBoxCloseResponse';
import { parseLuxuryBox } from './venuesServices';
import CloverPointUpdatePayload from 'types/requests/CloverPointUpdatePayload';

export const getCloverPointConfigs = async (page?: number): Promise<PaginationData<CloverPointConfig> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: `payments/clover-point/configs?page=${page ?? 1}`,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseCloverPointConfigs(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const storeCloverPointConfig = async (
  cloverPointConfig: CloverPointConfig,
): Promise<CloverPointConfig | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: 'payments/clover-point/config',
      data: cloverPointConfig,
    });
    return parseCloverPointConfig(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateCloverPointConfig = async (
  cloverPointConfigId: string,
  cloverPointConfig: CloverPointConfig,
): Promise<CloverPointConfig | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `payments/clover-point/config/${cloverPointConfigId}`,
      data: cloverPointConfig,
    });
    return parseCloverPointConfig(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getCloverPoints = async (
  cloverPointConfigId: string,
  page?: number,
  searchTerm?: string,
): Promise<PaginationData<CloverPoint> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: `payments/clover-point/config/${cloverPointConfigId}/clover-points?page=${
        page ?? 1
      }&searchTerm=${searchTerm}`,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseCloverPoints(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateCloverPoint = async (
  cloverPointConfigId: string,
  cloverPoint: CloverPointUpdatePayload,
  luxuryBoxIds: string[],
): Promise<CloverPoint | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `payments/clover-point/config/${cloverPointConfigId}/clover-points/${cloverPoint.id}`,
      data: { ...cloverPoint, luxuryBoxIds },
    });
    return parseCloverPoint(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateCloverPoints = async (
  cloverPointConfigId: string,
  merchantId: string,
  cloverAccessToken: string,
  cloverServerUrl: string,
): Promise<CloverPointConfig | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `payments/clover-point/config/${cloverPointConfigId}/update-clover-points`,
      data: {
        merchantId,
        cloverAccessToken,
        cloverServerUrl,
      },
    });
    return parseCloverPointConfig(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getAvailableCloverPoints = async (cloverPointConfig: string): Promise<CloverPoint[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'GET',
      url: `clover-point/config/${cloverPointConfig}/available-clover-points`,
    });
    return parseCloverPoints(data);
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const getCashBoxes = async (formData: any, page?: number): Promise<PaginationData<CashBox> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'post',
      url: `clover-point/cash-boxes?page=${page ?? 1}`,
      data: {
        ...formData,
      },
    });
    return {
      currentPage: paginationData.current_page,
      data: parseCashBoxes(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getCashBoxesReports = async ({
  formData,
  lang,
}: {
  formData: any;
  lang: string;
}): Promise<Blob | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `clover-point/cash-boxes/reports`,
      data: {
        ...formData,
        download: true,
        lang,
      },
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const reOpenCashBox = async (cashBoxId: number): Promise<boolean> => {
  try {
    await axiosInstance({
      method: 'put',
      url: `clover-point/cash-boxes/${cashBoxId}/re-open`,
    });
    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
};

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export const parseCashBoxes = (data: any): CashBox[] => {
  return data.map((item: any): CashBox => parseCashBox(item));
};

const parseCashBox = (data: any): CashBox => {
  return {
    id: data.id,
    cloverPointId: data.clover_point_id,
    cashBalance: data.cash_balance,
    qrBalance: data.qr_balance,
    cardBalance: data.card_balance,
    createdAt: data.created_at,
    closedAt: data.closed_at,
  };
};

export const getCashBoxEventsByCashBoxId = async (cashBoxId: number): Promise<CashBoxEvent[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `clover-point/cash-boxes/events`,
      data: {
        cashBoxId,
      },
    });
    return parseCashBoxEvents(data);
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const getCashBoxDetails = async (cashBoxId: number): Promise<CashBoxCloseResponse | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `clover-point/cash-boxes/details`,
      data: {
        cashBoxId,
      },
    });
    return parseCashBoxCloseResponse(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const parseCashBoxEvents = (data: any): CashBoxEvent[] => {
  return data.map((item: any): CashBoxEvent => parseCashBoxEvent(item));
};

export const parseCashBoxEvent = (data: any): CashBoxEvent => {
  return {
    type: data.type,
    data:
      data.type === CashBoxEventsTypes.Transaction
        ? parseTransaction(data.data)
        : data.type === CashBoxEventsTypes.Order
        ? parseOrder(data.data)
        : parseCashBoxAction(data.data),
    createdAt: data.created_at,
  };
};

export const parseTransaction = (data: any): CashBoxTransaction => {
  return {
    id: data.id,
    total: data.amount,
    createdAt: data.created_at,
    type: data.type,
  };
};

export const parseCashBoxAction = (data: any): CashBoxAction => {
  return {
    id: data.id,
    cashBoxId: data.cash_box_id,
    userId: data.user_id,
    type: data.type,
    createdAt: data.created_at,
  };
};

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export const parseCloverPointConfigs = (data: any): CloverPointConfig[] => {
  return data.map((item: any): CloverPointConfig => parseCloverPointConfig(item));
};

const parseCloverPointConfig = (data: any): CloverPointConfig => {
  return {
    id: data.id,
    description: data.description,
    applicationId: data.application_id,
    accessToken: data.access_token,
    cloverServer: data.clover_server,
    merchantId: data.merchant_id,
    friendlyId: data.friendly_id,
    usesSitefApp: data.uses_sitef_app === 1,
    merchantTaxId: data.merchant_tax_id ?? undefined,
    isvTaxId: data.isv_tax_id ?? undefined,
  };
};

export const parseCloverPoints = (data: any): CloverPoint[] => {
  return data.map((item: any): CloverPoint => parseCloverPoint(item));
};

export const parseCloverPoint = (data: any): CloverPoint => {
  return {
    id: data.id,
    description: data.description,
    model: data.model,
    isPaired: data.is_paired === 1,
    acceptsCash: data.accepts_cash === 1,
    isAutoService: Boolean(data.is_auto_service),
    isUsedOnLuxuryBox: data.is_used_on_luxury_box === 1,
    supportsOffline: Boolean(data.supports_offline),
    luxuryBox: data.luxury_boxes && data.luxury_boxes.length > 0 ? parseLuxuryBox(data.luxury_boxes[0]) : undefined,
    name: data.name ?? '',
    deviceAddress: data.device_address ?? '',
    serial: data.serial ?? '',
  };
};

export const parseCashBoxCloseResponse = (data: any): CashBoxCloseResponse => {
  return {
    transactions: {
      depositTotal: data.transactions.deposit_total,
      withdrawalTotal: data.transactions.withdrawal_total,
      events: [],
    },
    totalPerPaymentMethod: {
      cashBalance: data.total_per_payment_method.cash_balance,
      qrBalance: data.total_per_payment_method.qr_balance,
      cardBalance: data.total_per_payment_method.card_balance,
    },
    productsPerCategory: data.products_per_category.map((item: any): CashBoxCloseCategoryGroup => {
      return {
        categoryName: item.category_name,
        products: item.products.map((product: any): CashBoxCloseProduct => {
          return {
            productId: product.product_id ?? '',
            productName: product.product_name ?? null,
            quantity: product.quantity,
            totalPrice: Number(product.total_price) || 0,
          };
        }),
      };
    }),
    refunds: data.refunds ? parseOrders(data.refunds) : [],
    total: data.total,
  };
};
